import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Filter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuidesPage = () => {
  const { language, t } = useLanguage();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');

  const categories = [
    { value: '', label: language === 'it' ? 'Tutte' : 'All' },
    { value: 'italy', label: language === 'it' ? 'Italia' : 'Italy' },
    { value: 'europe', label: language === 'it' ? 'Europa' : 'Europe' },
    { value: 'asia', label: 'Asia' },
    { value: 'oceania', label: 'Oceania' }
  ];

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const params = selectedCategory ? { category: selectedCategory } : {};
        const response = await axios.get(`${API}/guides`, { params });
        setGuides(response.data);
      } catch (error) {
        console.error('Error fetching guides:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGuides();
  }, [selectedCategory]);

  return (
    <main className="pt-20 min-h-screen bg-[#0A0A0A]" data-testid="guides-page">
      {/* Header */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-center"
          >
            {/* Launch Badge */}
            <div className="inline-flex items-center gap-2 bg-[#C9B896]/10 border border-[#C9B896]/20 px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-[#C9B896] rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-[#C9B896]">
                {language === 'it' ? 'Offerta Lancio' : 'Launch Offer'}
              </span>
            </div>
            <h1 className="font-bold tracking-tight text-4xl md:text-5xl mb-4 text-white">
              {t.guides.title}
            </h1>
            <p className="text-lg text-[#888] max-w-xl mx-auto">
              {t.guides.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-[#1C1C1E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Filter className="w-5 h-5 text-[#888] mr-2" />
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.value
                    ? 'bg-[#C9B896] text-white'
                    : 'bg-[#1C1C1E] text-white hover:bg-[#2C2C2E]'
                }`}
                data-testid={`filter-${cat.value || 'all'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-[200px] bg-[#1C1C1E] rounded-2xl mb-4" />
                  <div className="h-6 bg-[#1C1C1E] rounded w-3/4 mb-2" />
                  <div className="h-4 bg-[#1C1C1E] rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {guides.map((guide, index) => {
                const title = language === 'it' ? guide.title_it : guide.title_en;
                const description = language === 'it' ? guide.description_it : guide.description_en;

                return (
                  <motion.article
                    key={guide.id}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="group"
                    data-testid={`guide-item-${guide.id}`}
                  >
                    <Link to={`/guide/${guide.id}`} className="block bg-[#1C1C1E] rounded-2xl overflow-hidden hover:bg-[#2C2C2E] transition-all duration-300">
                      {/* Image */}
                      <div className="relative h-[200px] overflow-hidden">
                        <img
                          src={guide.image_url}
                          alt={title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        
                        {guide.featured && (
                          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            <span className="text-xs font-medium text-white">{language === 'it' ? 'In Evidenza' : 'Featured'}</span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <h2 className="text-lg font-semibold text-white mb-2 group-hover:text-[#C9B896] transition-colors line-clamp-2">
                          {title}
                        </h2>
                        <p className="text-[#888] text-sm mb-4 line-clamp-2">
                          {description}
                        </p>

                        {/* Price and CTA */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {guide.original_price && guide.original_price > guide.price && (
                              <span className="text-sm text-[#666] line-through">
                                €{guide.original_price.toFixed(2).replace('.', ',')}
                              </span>
                            )}
                            <span className="text-xl font-bold text-white">
                              €{guide.price.toFixed(2).replace('.', ',')}
                            </span>
                          </div>
                          <span className="flex items-center gap-1 text-[#C9B896] text-sm font-medium group-hover:gap-2 transition-all">
                            {language === 'it' ? 'Scopri' : 'Explore'}
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                );
              })}
            </div>
          )}

          {!loading && guides.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <p className="text-[#888] text-lg">
                {language === 'it' ? 'Nessuna guida trovata' : 'No guides found'}
              </p>
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
};

export default GuidesPage;
